import { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { type BulkResult, bulkArchivePractitioners } from "@/api/practitioners";

type Props = {
  open: boolean;
  ids: string[];
  onClose: () => void;
  onDone: () => void;
};

export function BulkArchiveDialog({ open, ids, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const [result, setResult] = useState<BulkResult | null>(null);

  const mutation = useMutation({
    mutationFn: () => bulkArchivePractitioners(ids),
    onSuccess: (r) => {
      setResult(r);
      onDone();
    },
  });

  const close = () => {
    setResult(null);
    mutation.reset();
    onClose();
  };

  const errorMsg =
    (mutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t("practitionersBulk.archiveTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!result && (
            <>
              <Alert severity="warning">
                {t("practitionersBulk.archiveConfirm", { count: ids.length })}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {t("practitionersBulk.archiveHint")}
              </Typography>
            </>
          )}
          {result && (
            <>
              <Alert severity="success">
                {t("practitionersBulk.archiveDone", {
                  processed: result.processed,
                  skipped: result.skipped,
                })}
              </Alert>
              <Box>
                <Stack direction="row" spacing={1}>
                  <Chip color="primary" label={`${t("practitionersBulk.requested")}: ${result.requested}`} />
                  <Chip color="success" label={`${t("practitionersBulk.processed")}: ${result.processed}`} />
                  <Chip color="default" label={`${t("practitionersBulk.skipped")}: ${result.skipped}`} />
                </Stack>
              </Box>
            </>
          )}
          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>
          {result ? t("common.close") : t("common.cancel")}
        </Button>
        {!result && (
          <Button
            variant="contained"
            color="warning"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || ids.length === 0}
            startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t("practitionersBulk.archiveCta")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
